/**
 * AD-021 — the pilot goes over the side, and comes down under a round canopy.
 *
 * **This is a declared port enhancement, not recovered behaviour.** Red Baron
 * (1990) has no bail-out: `SIMEND.DAT`'s eleven outcomes contain no such
 * ending, the career model is permadeath, and the game's own manual says why —
 * *"Aside from their balloon observers, the Allies never issued these
 * lifesaving devices to their aviators"*
 * (`docs/game/manual/02-historical-overview.md`). The user asked for it anyway
 * (2026-08-27), which is the standing AD-018's burning buildings, AD-020's
 * building collisions and the dive whine already ship on. The full account,
 * including the historical footnote that makes it palatable, is
 * `docs/engineering/bail-out-design.md`.
 *
 * ## What this module is, and what it deliberately is not
 *
 * A descending pilot is **not an aeroplane**. He has no airframe, no engine, no
 * control surfaces and no `SimState`; he has a position, a velocity and a
 * canopy. So this is its own small integrator on `EncounterState` rather than
 * anything routed through `flight/step.ts`, and the aeroplane he left is flown
 * down by AD-014's existing derelict arc with nothing added to it
 * (`derelict.ts`'s `isWreck` gained the third case and that is the entire
 * coupling).
 *
 * ## The descent rate is 5.5 m/s, and the number this port already had is not it
 *
 * `flight-model-design.md` §AD-014 records a **10–16 m/s "parachute descent"**
 * and `damage.ts` repeats the phrase. That is a *deep-stalled airframe*
 * behaving like a bluff body, and the word is a metaphor there — it is roughly
 * twice what a canopy actually does. A 28-ft flat-circular silk canopy of the
 * period lands a man at **5–6 m/s**, which is a survivable arrival and the
 * reason the things worked. `PARACHUTE_DESCENT_MPS` is set at 5.5 and
 * `parachute.test.ts` measures the settled rate against that band rather than
 * restating it in a comment (`CLAUDE.md`'s rule, and `lessons.md`'s
 * doomed-descent entry is what happens when a design doc's timing is not
 * pinned).
 *
 * ## Drift is the wind model's, not a second one
 *
 * A round canopy has no forward drive and no steering: it goes where the air
 * goes. So the horizontal target velocity **is** `windVelocity(...)`, sampled
 * exactly as `flight/step.ts` samples it — same bands, same weather, same
 * `realisticWeather` flag — and there is no second answer anywhere for how hard
 * it is blowing at 1,200 m. With REALISTIC WEATHER off the wind is zero and he
 * comes straight down, which is the same bargain the aeroplane has.
 *
 * ## Determinism
 *
 * Pure and RNG-free: every quantity is a function of the previous state, `dt`,
 * the ground query and the sim config that was captured at the moment of the
 * jump. Two peers stepping the same encounter get the same canopy. The state is
 * `undefined` until somebody jumps, and `encounter-hash.ts` drops undefined
 * keys — so an encounter in which nobody bails hashes to the string it did
 * before this module existed.
 */

import type { Vec3 } from "../flight/math3d.ts";
import type { SimState } from "../flight/state.ts";
import { windVelocity, type WeatherKind } from "../flight/wind.ts";
import type { Tunables } from "../flight/tunables.ts";

/**
 * Where a jumped pilot is in the sequence.
 *
 * - `falling` — out of the aeroplane, canopy not yet blossomed.
 * - `open` — under silk, descending at `PARACHUTE_DESCENT_MPS` and drifting.
 * - `landed` — down alive. What happens next is the mission layer's
 *   (`SAFE LANDING` on our side, `BEHIND THE LINES` on theirs).
 * - `dead` — reached the ground before the canopy took his weight. This is the
 *   cost that makes bailing a decision rather than a free undo; see
 *   `PARACHUTE_PULL_SECONDS`.
 */
export const PARACHUTE_STATUSES = ["falling", "open", "landed", "dead"] as const;

export type ParachuteStatus = (typeof PARACHUTE_STATUSES)[number];

export interface ParachuteState {
  /** The aeroplane he left, so the app can hide its seated `PILOT` node and the mission layer can name him. */
  readonly pilotId: string;
  readonly position: Vec3;
  readonly velocity: Vec3;
  readonly status: ParachuteStatus;
  /** Seconds since he left the aeroplane. Drives the canopy opening and nothing else. */
  readonly fallSeconds: number;
  /** The weather this jump was made in, captured once — a canopy carries no `SimConfig`. */
  readonly weather: WeatherKind;
  /** REALISTIC WEATHER, as the aeroplane resolved it. Off means no drift, same as the aeroplane. */
  readonly windEnabled: boolean;
  readonly tunables: Tunables;
}

/**
 * Free-fall seconds before the canopy takes his weight.
 *
 * **Invented, and stated as such.** A period Heinecke was a static-line-ish
 * pull off the seat pack and the modern figure for a low-level manual pull is
 * two to three seconds to a full canopy; 2.5 is chosen as the round number in
 * that range. What it buys is the only thing that matters here: a minimum
 * height. Measured out of the integrator rather than reasoned about: a Camel
 * pilot who jumps below **44 m AGL does not survive it**, and that is
 * `PARACHUTE_MIN_SAFE_AGL_M`, which `parachute.test.ts` walks metre by metre
 * rather than restating. (It is the Camel's figure because the throw he leaves
 * with is the aeroplane's own speed; a faster machine costs him a little more
 * height, a slower one a little less.)
 */
export const PARACHUTE_PULL_SECONDS = 2.5;

/**
 * The settled rate under a full canopy, m/s. See the module note on why this is
 * *not* the 10–16 m/s the flight model calls a "parachute descent".
 */
export const PARACHUTE_DESCENT_MPS = 5.5;

/** Free-fall terminal velocity for a man, m/s — the textbook belly-to-earth figure. */
export const PARACHUTE_FREEFALL_TERMINAL_MPS = 55;

/**
 * Velocity relaxation time constants, seconds. The falling one is loose (a body
 * takes many seconds to reach terminal); the open one is short and is what the
 * opening shock reads as.
 */
export const PARACHUTE_FREEFALL_TAU_S = 6;
export const PARACHUTE_OPEN_TAU_S = 0.8;

/**
 * The fastest arrival a man walks away from, m/s.
 *
 * **This, and not the canopy flag, is what decides whether a jump worked.** An
 * early draft called any arrival with the canopy open a landing, which let a
 * pilot who jumped at 30 m hit the ground at 18 m/s and be reported as having
 * landed safely — a canopy that has begun to open is not a canopy that has
 * taken your weight. Trained parachute-landing falls are made at 5–7 m/s and 8
 * is the generous edge of that; above it he has arrived at the speed of a fall
 * from a two-storey roof.
 *
 * It is measured against his **whole** velocity and not the vertical component
 * alone, which the second draft got wrong too: a man who steps out at 1 m still
 * carries the aeroplane's 41 m/s along the ground, and a test that only looked
 * down called that a safe landing.
 */
export const PARACHUTE_SURVIVABLE_ARRIVAL_MPS = 8;

/**
 * The height below which a jump is not survivable, metres AGL.
 *
 * **Derived, not chosen.** It is whatever `PARACHUTE_PULL_SECONDS` of free fall
 * plus the deceleration to `PARACHUTE_SURVIVABLE_ARRIVAL_MPS` happens to cost,
 * and the only honest way to know it is to run the integrator — so
 * `parachute.test.ts` walks the real descent for the lowest jump that still
 * ends `landed` and asserts this constant matches what it finds, rather than
 * the two being free to drift apart. Nothing reads it; it exists so the
 * design doc's minimum-height claim has an assertion behind it.
 */
export const PARACHUTE_MIN_SAFE_AGL_M = 44;

function relax(current: number, target: number, tau: number, dt: number): number {
  // Exponential approach, integrated exactly rather than by Euler step, so the
  // result does not depend on the tick rate.
  const blend = 1 - Math.exp(-dt / tau);
  return current + (target - current) * blend;
}

/**
 * The velocity the canopy (or the falling body) is heading towards this tick:
 * horizontally the air itself, vertically either terminal velocity or the
 * canopy's settled rate.
 */
function targetVelocity(state: ParachuteState, canopyOpen: boolean): Vec3 {
  const wind = windVelocity({
    altitudeM: state.position.y,
    grounded: false,
    enabled: state.windEnabled,
    weather: state.weather,
    tunables: state.tunables,
  });
  return {
    x: wind.x,
    y: -(canopyOpen ? PARACHUTE_DESCENT_MPS : PARACHUTE_FREEFALL_TERMINAL_MPS),
    z: wind.z,
  };
}

/**
 * True while the pilot is still in the air — falling, or under silk.
 *
 * This is the **one** predicate the rest of the engine asks, and it is asked in
 * three places that each mean the same thing by it: the encounter must not
 * declare the player down (`encounter.ts`), the mission must not freeze
 * (`mission-run.ts`), and the app must keep the camera on him. Deliberately one
 * helper and not three inline `status === …` tests — `lessons.md`'s
 * doomed-descent entry is about precisely that duplication drifting apart.
 */
export function parachuteAloft(state: ParachuteState | undefined): boolean {
  return state !== undefined && (state.status === "falling" || state.status === "open");
}

export interface ParachuteJump {
  readonly pilotId: string;
  /** The aeroplane's state at the moment he left it — he inherits its position and velocity. */
  readonly sim: SimState;
}

/**
 * The moment of the jump. He leaves with the aeroplane's own velocity, which is
 * why bailing out of a spin throws him harder than bailing out of a glide, and
 * why the drift he ends up with is not simply the wind.
 */
export function beginJump(jump: ParachuteJump): ParachuteState {
  const { sim } = jump;
  return {
    pilotId: jump.pilotId,
    position: { ...sim.position },
    velocity: { ...sim.velocity },
    status: "falling",
    fallSeconds: 0,
    weather: sim.config.weather,
    windEnabled: sim.config.realism.realisticWeather,
    tunables: sim.config.tunables,
  };
}

export interface ParachuteStep {
  readonly dt: number;
  /** Ground height under the pilot, from the encounter's own `GroundQuery` — never a second surface. */
  readonly groundHeightM: number;
}

/**
 * One tick of the descent. Returns the state unchanged once he is down, so a
 * caller may keep stepping a finished canopy without it moving.
 */
export function stepParachute(state: ParachuteState, step: ParachuteStep): ParachuteState {
  if (state.status === "landed" || state.status === "dead") return state;

  const fallSeconds = state.fallSeconds + step.dt;
  const canopyOpen = fallSeconds >= PARACHUTE_PULL_SECONDS;
  const target = targetVelocity(state, canopyOpen);
  const tau = canopyOpen ? PARACHUTE_OPEN_TAU_S : PARACHUTE_FREEFALL_TAU_S;

  const velocity: Vec3 = {
    x: relax(state.velocity.x, target.x, tau, step.dt),
    // No separate gravity term: terminal velocity *is* where gravity and drag
    // balance, so it is already the target, and integrating `g` on top would
    // count it twice and sink him at an accelerating rate for ever.
    y: relax(state.velocity.y, target.y, tau, step.dt),
    z: relax(state.velocity.z, target.z, tau, step.dt),
  };

  const position: Vec3 = {
    x: state.position.x + velocity.x * step.dt,
    y: state.position.y + velocity.y * step.dt,
    z: state.position.z + velocity.z * step.dt,
  };

  const next = { ...state, position, velocity, fallSeconds, status: canopyOpen ? "open" : "falling" } as const;
  if (position.y > step.groundHeightM) return next;

  // He has arrived. Whether that is a landing is decided by **how fast**, not by
  // whether the canopy had begun to open — see
  // `PARACHUTE_SURVIVABLE_ARRIVAL_MPS`. This is what makes the minimum jump
  // height fall out of the physics instead of being a number somebody picked.
  const arrivalMps = Math.hypot(velocity.x, velocity.y, velocity.z);
  return {
    ...next,
    position: { ...position, y: step.groundHeightM },
    velocity: { x: 0, y: 0, z: 0 },
    status: arrivalMps <= PARACHUTE_SURVIVABLE_ARRIVAL_MPS ? "landed" : "dead",
  };
}
